import { useState } from 'react';
import type { SortKey } from '@/types/movie';
import { SORT_OPTIONS, YEAR_OPTIONS } from '@/data/catalog';

export interface FilterValue {
  region: string;
  year: string;
  sort: SortKey;
}

interface FilterBarProps {
  value: FilterValue;
  onChange: (next: FilterValue) => void;
  regions: string[];
  onReset?: () => void;
}

interface OptionGroupProps {
  label: string;
  options: { value: string; label: string }[];
  active: string;
  onSelect: (value: string) => void;
}

function OptionGroup({ label, options, active, onSelect }: OptionGroupProps) {
  return (
    <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:gap-4">
      <span className="mt-1.5 shrink-0 text-xs font-medium text-foreground-400 sm:w-12">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const isActive = active === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onSelect(option.value)}
              className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm transition-colors ${
                isActive
                  ? 'bg-primary-500 font-medium text-background-50'
                  : 'text-foreground-600 hover:bg-background-100 hover:text-primary-600'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterBar({ value, onChange, regions, onReset }: FilterBarProps) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const regionOptions = [{ value: 'all', label: '全部' }, ...regions.map((region) => ({ value: region, label: region }))];
  const yearOptions = [{ value: 'all', label: '全部' }, ...YEAR_OPTIONS.map((year) => ({ value: String(year), label: `${year}` }))];
  const sortOptions = SORT_OPTIONS.map((option) => ({ value: option.key, label: option.label }));

  const update = (patch: Partial<FilterValue>) => onChange({ ...value, ...patch });

  const activeLabel = [
    value.region === 'all' ? '全部地区' : value.region,
    value.year === 'all' ? '全部年份' : `${value.year}年`,
    SORT_OPTIONS.find((option) => option.key === value.sort)?.label || '综合排序',
  ].join(' · ');

  return (
    <>
      {/* desktop */}
      <div className="hidden rounded-lg border border-background-200 bg-background-50 px-5 py-1 md:block">
        <OptionGroup label="地区" options={regionOptions} active={value.region} onSelect={(region) => update({ region })} />
        <div className="h-px bg-background-100" />
        <OptionGroup label="年份" options={yearOptions} active={value.year} onSelect={(year) => update({ year })} />
        <div className="h-px bg-background-100" />
        <OptionGroup label="排序" options={sortOptions} active={value.sort} onSelect={(sort) => update({ sort: sort as SortKey })} />
        {onReset && (
          <div className="border-t border-background-100 py-3 text-right">
            <button
              type="button"
              onClick={onReset}
              className="inline-flex cursor-pointer items-center gap-1 text-xs text-foreground-400 transition-colors hover:text-accent-600"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        )}
      </div>

      {/* mobile */}
      <div className="md:hidden">
        <div className="flex items-center gap-2">
          <div className="flex-1 truncate rounded-md border border-background-200 bg-background-50 px-3 py-2 text-xs text-foreground-600">
            {activeLabel}
          </div>
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md bg-primary-500 px-3.5 py-2 text-xs font-medium text-background-50"
          >
            <i className="ri-filter-3-line text-sm" />
            筛选
          </button>
        </div>

        {sheetOpen && (
          <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="筛选条件">
            <button
              type="button"
              aria-label="关闭筛选"
              onClick={() => setSheetOpen(false)}
              className="absolute inset-0 cursor-pointer bg-primary-950/50"
            />
            <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 px-4 pb-6 pt-3">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-medium text-foreground-950">筛选与排序</h3>
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-500"
                  aria-label="关闭"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>
              <div className="divide-y divide-background-100">
                <OptionGroup label="地区" options={regionOptions} active={value.region} onSelect={(region) => update({ region })} />
                <OptionGroup label="年份" options={yearOptions} active={value.year} onSelect={(year) => update({ year })} />
                <OptionGroup label="排序" options={sortOptions} active={value.sort} onSelect={(sort) => update({ sort: sort as SortKey })} />
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    onChange({ region: 'all', year: 'all', sort: 'default' });
                    onReset?.();
                  }}
                  className="flex-1 cursor-pointer rounded-md border border-background-300 py-2.5 text-sm text-foreground-600"
                >
                  重置
                </button>
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  className="flex-1 cursor-pointer rounded-md bg-primary-500 py-2.5 text-sm font-medium text-background-50"
                >
                  查看结果
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}